/* Copyright (c) 2020-present CloudIO Inc */

import {
  DataRecord,
  DATE_OPS_KEY_TYPE,
  MULTIPLE_DATE_OPS,
  OPS_ICONS,
} from '@cloudio-saas/datasource-types';
import {
  IconButton,
  InputAdornment,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  TextField,
  TextFieldProps,
} from '@mui/material';
import clsx from 'clsx';
import {
  BaseProps,
  Form,
  isNotEmpty,
  SearchDateField,
} from 'componentregistry';
import { useCurrentDataStore } from 'components/DataStoreReference/DataStoreReference';
import { useDateFieldStyles } from 'components/DateField/DateField';
import { HorizontalFormField } from 'components/Form/FormUtils';
import { allDateOps, hasDateEditor } from 'components/SmartSearch/Operators';
import {
  getName,
  getOperator,
  getOpOptions,
  getValue,
} from 'components/SmartSearch/SearchUtils';
import { FieldWrapper, useFieldVariant } from 'components/TextField/BaseField';
import { DateInputWithTZ } from 'custom/DateInputWithTZ';
import { addDays, format as formatDate, isValid, parseISO } from 'date-fns';
import Icon from 'icons/Icon';
import React, { useEffect, useId, useState } from 'react';
import { useUserDateFormat, useUserDateTimeFormat } from 'root/useUser';
import { useStoreAttributeFilter } from 'store/useAppStore';
import { isEmpty, isNull } from 'utils/isEmpty';
import { useTranslate } from '../../utils/lang';
import { useFocus } from 'hooks/useFocus';
import { useStringExpression } from 'expression/useExpression';
import zIndex from 'constants/ZIndex';

interface DateFieldProps
  extends Required<SearchDateField.SearchDateField>,
    BaseProps<'SearchDateField'> {}

interface Props extends DateFieldProps, Form.HorizontalFormProps {
  headerFilter?: boolean;
  onHeaderFilterSearch?: () => void;
}

function IOSearchDateField(
  props: Props,
  ref: React.Ref<HTMLInputElement>,
): React.ReactElement {
  const {
    appUid,
    autoFocus,
    children,
    className,
    defaultOperator,
    domProps,
    flex,
    formHorizontal,
    format,
    hideOperatorChooser,
    labelAlignment,
    labelWidth,
    required,
    showTime,
    viewAttribute,
    platform,
    itemId,
    headerFilter,
    pageId,
    onHeaderFilterSearch,
  } = props;

  let { helperText, placeholder } = props;
  const { label } = props;
  const { t } = useTranslate();
  helperText = t(helperText);
  let labelResult =
    useStringExpression({
      exp: label,
      context: {
        appUid,
        pageId,
        itemId,
        property: 'label',
        data: {},
      },
    }) || undefined;
  placeholder = t(placeholder);

  const inputRef = useFocus(itemId);
  const classes = useDateFieldStyles();
  const variant = useFieldVariant(appUid);

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
    if (platform.isRecording()) {
      platform.recordLine(
        itemId,
        `await ui.click({ testid: '${itemId}-op' });`,
      );
    }
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const [ops, setOps] = useState<DATE_OPS_KEY_TYPE>(defaultOperator);
  const [value, setValue] = useState('');
  const [toValue, setToValue] = useState('');
  const options = getOpOptions('Date', t);

  const store = useCurrentDataStore<DataRecord>();
  const attr = store?.getAttribute(viewAttribute);
  const filter = useStoreAttributeFilter<DataRecord>(store, viewAttribute);
  useEffect(() => {
    if (filter) {
      const op = getOperator(filter) as DATE_OPS_KEY_TYPE;
      setOps(op);
      const _hasEditor = hasDateEditor(op);
      if (!_hasEditor) {
        setValue('');
      } else {
        const val = getValue(filter) as string | string[];
        if (op in MULTIPLE_DATE_OPS) {
          if (Array.isArray(val)) {
            setValue(val[0]);
            if (val.length > 1) {
              setToValue(val[1]);
            } else {
              setToValue('');
            }
          } else {
            setValue(val);
            setToValue('');
          }
        } else {
          setToValue('');
          if (Array.isArray(val)) {
            setValue(val[0]);
          } else {
            setValue(val);
          }
        }
      }
    } else {
      // #974 Fix. Any filter other than values has to be set to default operator
      setOps(defaultOperator);
      setValue('');
      setToValue('');
    }
  }, [defaultOperator, filter]);
  const hasEditor = hasDateEditor(ops);

  function updateFilter(
    _attr: string,
    op: DATE_OPS_KEY_TYPE,
    val: string,
    toVal: string,
  ) {
    if (!store) return;

    let filters = store.getSmartSearchFilter();
    filters = filters.filter((f) => getName(f) !== _attr);
    const _hasEditor = hasDateEditor(op);
    if (!_hasEditor || isNotEmpty(val)) {
      let v: string | string[] = _hasEditor ? val : new Date().toISOString();
      if (op in MULTIPLE_DATE_OPS) {
        v = '';
        if (isNotEmpty(val) && isNotEmpty(toVal)) {
          v = [val, toVal];
        }
      }
      if (isNotEmpty(v)) {
        const _filter = {
          [_attr]: { [op]: v },
        };
        filters.push(_filter);
      }
    } else {
      setValue('');
      setToValue('');
    }

    store?.setSmartSeachFilter(filters);
  }

  const handleDateChange = async (date: Date | null) => {
    const newValue = date && isValid(date) ? date.toISOString() : '';
    if (value && toValue && toValue === newValue) {
      return;
    }
    let toVal = '';
    let val = '';
    if (ops in MULTIPLE_DATE_OPS) {
      if (value) {
        if (isEmpty(toValue)) {
          val = value;
          toVal = newValue;
          setValue(val);
          setToValue(toVal);
          updateFilter(viewAttribute, ops, val, toVal);
        } else {
          setValue(newValue);
          setToValue('');
        }
      } else {
        setValue(newValue);
        setToValue('');
      }
    } else {
      val = newValue;
      toVal = '';
      setValue(newValue);
      setToValue('');
      updateFilter(viewAttribute, ops, val, toVal);
    }

    if (platform.isRecording() && !headerFilter) {
      platform.recordLine(itemId, `await ui.type('${itemId}', new Date());`);
    }
  };

  const dateFormat = useUserDateFormat();
  const dateTimeFormat = useUserDateTimeFormat();

  let val = value;
  if (ops in MULTIPLE_DATE_OPS) {
    val = '';
    if (toValue && value) {
      const fmt = format || (showTime ? dateTimeFormat : dateFormat);
      placeholder = `${formatDate(parseISO(value), fmt)} - ${formatDate(
        parseISO(toValue),
        fmt,
      )}`;
    } else if (!value) {
      placeholder = 'Enter From Date';
    } else {
      placeholder = 'Enter To Date';
    }
  }

  const id = useId();

  const textFieldProps = (p: JSX.IntrinsicAttributes & TextFieldProps) => {
    return {
      id,
      ...p,
      onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
          // onBlur();
          onHeaderFilterSearch?.();
        }
      },
      onFocus: (event: React.FocusEvent<HTMLInputElement>) => {},
      InputProps: {
        ...p.InputProps,
        startAdornment: hideOperatorChooser ? undefined : (
          <InputAdornment position="start">
            <IconButton
              data-testid={`${itemId}-op`}
              onClick={handleClick}
              data-tip={allDateOps[ops] || ops}
            >
              <Icon iconName={OPS_ICONS[ops] || 'equals'} />
            </IconButton>
          </InputAdornment>
        ),
      },
      inputProps: {
        ...p.inputProps,
        placeholder: hasEditor
          ? placeholder || p.inputProps?.placeholder
          : allDateOps[ops],
      },
    };
  };

  const onBlur = (event: React.FocusEvent<HTMLInputElement>) => {};

  const dateFieldProps = {
    renderInput: (p: JSX.IntrinsicAttributes & TextFieldProps) => (
      <TextField
        fullWidth
        autoFocus={autoFocus}
        helperText={helperText}
        variant={variant}
        {...textFieldProps(p)}
        required={required}
      />
    ),
    inputRef,
    // @ts-ignore PopperProps
    PopperProps: {
      className: clsx(classes.popper, 'custom-datepicker-dialog'),
    },
    inputFormat: format || (showTime ? dateTimeFormat : dateFormat),
    value: val || null,
    onChange: (date: Date | null) => {
      if (isNull(date) || ops !== 'bn') {
        handleDateChange(date);
      }
    },
    onAccept: (date: Date | null) => {
      handleDateChange(date);
    },
    required,
    showTime,
    onClose: onBlur,
    autoFocus,
    disabled: !hasEditor,
    key: val ? 'date' : 'empty',
    attr,
    minDate:
      value && !toValue && allDateOps[ops] === 'is between'
        ? showTime
          ? new Date(value)
          : addDays(new Date(value), 1)
        : undefined,
  };
  labelResult =
    ops === 'on' ? labelResult : `${labelResult} (${allDateOps[ops]})`;

  return (
    <>
      <FieldWrapper
        className={clsx(className, classes.overflowHidden, classes.dateField)}
        domProps={domProps}
        flex={flex}
        ref={ref}
      >
        {formHorizontal && !headerFilter ? (
          <HorizontalFormField
            control={<DateInputWithTZ {...dateFieldProps} />}
            label={labelResult}
            labelAlignment={labelAlignment}
            labelWidth={labelWidth}
            required={required}
            id={id}
          />
        ) : (
          <>
            <DateInputWithTZ
              {...dateFieldProps}
              label={headerFilter ? undefined : labelResult}
              hiddenLabel={headerFilter}
            />
          </>
        )}
        {children}
      </FieldWrapper>
      <Menu
        id="basic-menu"
        anchorEl={anchorEl}
        open={!!anchorEl}
        onClose={handleClose}
        MenuListProps={{
          'aria-labelledby': 'basic-button',
        }}
        sx={{ zIndex: zIndex.Tooltip + 1 }}
      >
        {options.map((option) => {
          return (
            <MenuItem
              data-testid={`op-${option.value}`}
              key={option.value}
              onClick={() => {
                const op = option.value as DATE_OPS_KEY_TYPE;
                setOps(op);
                handleClose();
                updateFilter(viewAttribute, op, value, toValue);
                if (hasDateEditor(op)) {
                  setTimeout(() => {
                    inputRef.current?.focus();
                  }, 1);
                } else {
                  onHeaderFilterSearch?.();
                }
              }}
            >
              <ListItemIcon>
                <Icon iconName={OPS_ICONS[option.value] || 'equals'} />
              </ListItemIcon>
              <ListItemText>{option.label}</ListItemText>
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
}

export default React.forwardRef(IOSearchDateField);
